import { Suspense, useState } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useRouter } from '@tanstack/react-router';
import {
  Box,
  Button,
  CodeBlock,
  Coin,
  Grid,
  Heading,
  Icon,
  InlineStack,
  Page,
  PageBody,
  Panel,
  PanelDescription,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Stack,
  Tabs,
  TabsList,
  TabsTab,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { copyToClipboard } from '@/hooks/user-actions/use-copy-to-clipboard';
import { useAnalytics } from '@/services/analytics';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { capitalize } from '@/utils/string';
import type { ObjectType } from '@/services/db/object-type/entity';
import { ObjectTypeHeader } from './components/object-type-header';
import { ObjectTypeResolver } from './object-type-resolver';
import { useObjectColumns } from './use-object-columns';
import * as styles from './object-type-guide.css';

const GUIDE_TABS = { trackApi: 'track', reverseEtl: 'reverse' } as const;
type GuideTab = (typeof GUIDE_TABS)[keyof typeof GUIDE_TABS];

function apiExampleJson(objectTypeId: string): string {
  return `{
  "type": "object",
  "identifiers": {
      "object_type_id": "${objectTypeId}",
      "object_id": "acme"
  },
  "action": "identify",
  "attributes": {
      "name": "acme",
      "bill_day": 15,
      "monthly_due": 100.00
  },
  "cio_relationships": [
      {
          "identifiers": {
              "id": "42"
          }
      },
      {
          "identifiers": {
              "email": "billing-contact@example.com"
          }
      }
  ]
}`;
}

type GuideResolvedProps = {
  objectType: ObjectType;
  workspaceId: number;
};

function GuideResolved({ objectType, workspaceId }: GuideResolvedProps) {
  const router = useRouter();
  const { hasPermission } = usePermissions();
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const environment = useEnvironmentOptional().data?.environment;
  const [activeTab, setActiveTab] = useState<GuideTab>(GUIDE_TABS.trackApi);
  const columns = useObjectColumns({
    workspaceId,
    objectTypeId: objectType.id,
    sentAttributes: objectType.sent_attributes ?? [],
    isSampleWorkspace: environment?.is_sample_workspace === true,
  });

  const apiCredentialsHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/api_credentials',
    params: { workspaceId },
  }).href;
  const connectDatabaseHref = router.buildLocation({
    to: '/workspaces/$workspaceId/journeys/integrations/all/overview',
    params: { workspaceId },
  }).href;

  const singular = capitalize(objectType.singular_name);

  const copyPageLink = async () => {
    void track('objects', 'guidePageLinkCopied', {});
    const ok = await copyToClipboard(window.location.href);
    snackbars.create({
      variant: ok ? 'default' : 'critical',
      id: `copy.object-guide-link.${ok ? 'success' : 'error'}`,
      message: ok ? 'Copied to clipboard' : 'Could not copy to clipboard',
    });
  };

  return (
    <>
      <DocumentTitle title={objectType.name} />
      <Page>
        <ObjectTypeHeader
          objectType={objectType}
          workspaceId={workspaceId}
          environment={environment}
          activeTab="guide"
          canViewSettings={hasPermission('journeys/object_types/view')}
          canCreate={hasPermission('journeys/objects/create')}
          canExport={hasPermission('journeys/exports/create')}
          canCreateRelationships={hasPermission('journeys/object_relationships/create')}
          displayedAttributes={columns.availableColumns.filter(
            (attribute) => columns.columnVisibility[attribute] !== false,
          )}
          exportFilters={null}
          isExportBroaderThanList={false}
          isListLoaded={true}
        />
        <PageBody>
          <Grid columns="minmax(0, 1fr) 25%" gap="300" alignItems="flex-start">
            <Panel>
              <PanelHeader>
                <PanelTitle level="2">Setup guide</PanelTitle>
                <PanelDescription>
                  Follow the instructions below to start adding {objectType.name} to your Workspace.
                </PanelDescription>
              </PanelHeader>

              <PanelHeader py="0">
                <PanelTitle as="div">
                  <Tabs
                    className={styles.tabsInPanel}
                    activeTab={activeTab}
                    onChange={(tab) => {
                      if (tab === GUIDE_TABS.trackApi || tab === GUIDE_TABS.reverseEtl) setActiveTab(tab);
                    }}
                    isBorderless
                  >
                    <TabsList>
                      <TabsTab value={GUIDE_TABS.trackApi}>Track API</TabsTab>
                      <TabsTab value={GUIDE_TABS.reverseEtl}>Reverse ETL</TabsTab>
                    </TabsList>
                  </Tabs>
                </PanelTitle>
              </PanelHeader>

              <PanelSection withBorder withPadding={false}>
                {activeTab === GUIDE_TABS.trackApi ? (
                  <Box p="300">
                    <Stack gap="200">
                      <Stack gap="100">
                        <Heading level="4">1. Send {singular} objects via the Customer.io Track API</Heading>
                        <Paragraph>
                          This is the preferred way to implement custom objects into your workspace since objects will
                          automatically be added, updated, deleted, etc.
                        </Paragraph>
                      </Stack>

                      <InlineStack gap="200" wrap>
                        <Button
                          href="https://docs.customer.io/api/track/#tag/Object-Schemas"
                          isExternal
                          size="sm"
                          icon="exit"
                          iconPosition="trailing"
                        >
                          View Track API Documentation
                        </Button>
                        {hasPermission('account/api_credentials/view') ? (
                          <Button variant="secondary" size="sm" href={apiCredentialsHref}>
                            View API Credentials
                          </Button>
                        ) : null}
                      </InlineStack>

                      <Stack gap="100">
                        <Heading level="5">Example API request</Heading>
                        <CodeBlock
                          language="json"
                          code={apiExampleJson(objectType.id)}
                          withLineFoldingControls={false}
                          onCopyButtonClick={() => void track('objects', 'apiExampleCopied', {})}
                        />
                      </Stack>

                      <Heading level="4">
                        2. That's it. Once we start receiving {singular} data, this guide will be removed 🎉
                      </Heading>
                    </Stack>
                  </Box>
                ) : (
                  <Box p="300">
                    <Stack gap="200">
                      <Stack gap="100">
                        <Heading level="4">1. Connect and configure your database</Heading>
                        <Paragraph color="subtle">
                          Once connected, you'll select tables with {singular} data and any other table with People data
                          that's related to {capitalize(objectType.name)}.
                        </Paragraph>
                      </Stack>

                      <InlineStack>
                        <Button variant="primary" href={connectDatabaseHref}>
                          Connect database
                        </Button>
                      </InlineStack>

                      <Heading level="4">
                        2. That's it. Once we start receiving {singular} data, this guide will be removed 🎉
                      </Heading>
                    </Stack>
                  </Box>
                )}
              </PanelSection>
            </Panel>

            <Panel backgroundColor="subtle">
              <PanelSection>
                <Stack gap="200" alignInline="center" textAlign="center">
                  <Coin size="md">
                    <Icon name="tip" />
                  </Coin>

                  <Stack gap="100" alignInline="center">
                    <PanelTitle level="3">Invite your team to help.</PanelTitle>
                    <Paragraph>Share a link to this page to get help adding {objectType.name}.</Paragraph>
                  </Stack>

                  <Button
                    size="sm"
                    variant="secondary"
                    icon="copy-to-clipboard"
                    iconPosition="trailing"
                    onClick={() => void copyPageLink()}
                  >
                    Copy page link
                  </Button>
                </Stack>
              </PanelSection>
            </Panel>
          </Grid>
        </PageBody>
      </Page>
    </>
  );
}

export default function ObjectTypeGuidePage() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <ObjectTypeResolver route="/workspaces/$workspaceId/objects/$slug/guide">
        {(objectType, workspaceId) => <GuideResolved objectType={objectType} workspaceId={workspaceId} />}
      </ObjectTypeResolver>
    </Suspense>
  );
}
